import { useState } from 'react';
import { Loader2, Play, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function FamilyRow({ family, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const p = family.parent;
  const rel = family.relationship;

  return (
    <div className="border rounded-lg">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-muted/30"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
        <span className="font-semibold font-mono">{family.parent_bros_id}</span>
        <span className="text-muted-foreground ml-auto text-xs">
          {family.children_count} child{family.children_count !== 1 ? 'ren' : ''}
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2">
          {/* Parent row */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="text-left px-2 py-1 font-medium">ID</th>
                  <th className="text-right px-2 py-1 font-medium">Client Total</th>
                  <th className="text-right px-2 py-1 font-medium">Supplier Total</th>
                  <th className="text-right px-2 py-1 font-medium">VAT</th>
                  <th className="text-left px-2 py-1 font-medium">Check-in</th>
                  <th className="text-left px-2 py-1 font-medium">Check-out</th>
                  <th className="text-left px-2 py-1 font-medium">Status</th>
                  <th className="text-left px-2 py-1 font-medium">Client</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b font-semibold bg-muted/20">
                  <td className="px-2 py-1 font-mono">{family.parent_bros_id}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{fmt(p.client_total)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{fmt(p.supplier_total)}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{fmt(p.vat)}</td>
                  <td className="px-2 py-1">{p.check_in || '-'}</td>
                  <td className="px-2 py-1">{p.check_out || '-'}</td>
                  <td className="px-2 py-1">{p.status || '-'}</td>
                  <td className="px-2 py-1">{p.client_name || p.company || '-'}</td>
                </tr>
                {family.children.map((c) => (
                  <tr key={c.bros_id} className="border-b last:border-0 text-muted-foreground">
                    <td className="px-2 py-1 font-mono pl-6">{c.bros_id}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmt(c.client_total)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmt(c.supplier_total)}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmt(c.vat)}</td>
                    <td className="px-2 py-1">{c.check_in || '-'}</td>
                    <td className="px-2 py-1">{c.check_out || '-'}</td>
                    <td className="px-2 py-1">{c.status || '-'}</td>
                    <td className="px-2 py-1">{c.client_name || c.company || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap gap-2">
            <Badge variant={
              rel === 'parent_equals_children_sum' ? 'destructive' :
              rel === 'parent_is_zero_shell' ? 'secondary' : 'outline'
            }>
              {rel.replace(/_/g, ' ')}
            </Badge>
            <Badge variant={family.dates_match ? 'secondary' : 'outline'}>
              dates {family.dates_match ? 'match' : 'differ'}
            </Badge>
            <Badge variant={family.client_names_match ? 'secondary' : 'outline'}>
              client {family.client_names_match ? 'match' : 'differ'}
            </Badge>
          </div>
        </div>
      )}
    </div>
  );
}

export function BookingsHierarchyCheckPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const runCheck = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.brosBookingsHierarchyCheck();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const cc = result?.classification_counts;
  const hasHierarchy = cc && cc.parent_with_children > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Bookings Hierarchy Check</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Detects parent/child relationships encoded in bros_id (e.g. ENQ-00182 vs ENQ-00182-1)
          and quantifies whether revenue is being double-counted.
        </p>

        <Button onClick={runCheck} disabled={loading}>
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Play className="h-4 w-4 mr-2" />
          )}
          Run check
        </Button>

        {result && (
          <div className="space-y-6">
            {/* Section 1: Sample bros_ids */}
            <Section title="Sample bros_ids">
              <div className="text-xs font-mono bg-muted/30 border rounded-lg p-2 max-h-32 overflow-y-auto break-all">
                {result.bros_id_samples?.join(', ') || 'None'}
              </div>
            </Section>

            {/* Section 2: Prefix distribution */}
            {result.prefix_distribution && (
              <Section title="Prefix distribution">
                <div className="overflow-x-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="text-left px-2 py-1.5 font-medium">Prefix</th>
                        <th className="text-right px-2 py-1.5 font-medium">Count</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(result.prefix_distribution)
                        .sort((a, b) => b[1] - a[1])
                        .map(([prefix, count]) => (
                          <tr key={prefix} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono">{prefix}</td>
                            <td className="px-2 py-1 text-right tabular-nums">{count.toLocaleString()}</td>
                          </tr>
                        ))
                      }
                    </tbody>
                  </table>
                </div>
              </Section>
            )}

            {/* Section 3: Classification counts */}
            {cc && (
              <Section title="Classification counts">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <CountCard label="Parent with children" value={cc.parent_with_children} />
                  <CountCard label="Parent only" value={cc.parent_only} />
                  <CountCard label="Child" value={cc.child} />
                  <CountCard label="Other / orphan" value={cc.other + (cc.orphan_children || 0)} />
                </div>
                {!hasHierarchy && (
                  <div className="flex items-center gap-2 mt-3 p-3 rounded-lg border border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-950 text-sm text-green-700 dark:text-green-300">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    No hierarchy in practice — heatmap and KPI counts are correct.
                  </div>
                )}
                {hasHierarchy && (
                  <div className="flex items-center gap-2 mt-3 p-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 text-sm text-amber-700 dark:text-amber-300">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    Hierarchy detected — review families below.
                  </div>
                )}
              </Section>
            )}

            {/* Section 4: Families (top 10) */}
            {result.families && result.families.length > 0 && (
              <Section title={`Families (top ${result.families.length} by parent amount)`}>
                <div className="space-y-2">
                  {result.families.map((f, i) => (
                    <FamilyRow key={f.parent_bros_id} family={f} defaultOpen={i === 0} />
                  ))}
                </div>
              </Section>
            )}

            {/* Section 5: Revenue impact */}
            {result.revenue_impact && (
              <RevenueImpactSection
                impact={result.revenue_impact}
                families={result.families}
              />
            )}

            {/* Section 6: Invoice pattern observations */}
            {result.invoice_pattern && (
              <InvoicePatternSection pattern={result.invoice_pattern} />
            )}

            {/* Section 7: Interpretation */}
            {result.interpretation && (
              <Section title="Interpretation">
                <div className="space-y-2">
                  {Object.entries(result.interpretation).map(([key, text]) => (
                    <div key={key} className="text-xs border rounded-lg p-2">
                      <p className="font-medium text-muted-foreground mb-1">{key.replace(/_/g, ' ')}</p>
                      <p>{text}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{title}</p>
      {children}
    </div>
  );
}

function CountCard({ label, value }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-bold tabular-nums">{(value ?? 0).toLocaleString()}</p>
    </div>
  );
}

function RevenueImpactSection({ impact, families }) {
  // Collect all currencies across categories
  const allCurrencies = new Set();
  for (const cat of Object.values(impact)) {
    if (cat && typeof cat === 'object') {
      Object.keys(cat).forEach((c) => allCurrencies.add(c));
    }
  }
  const currencies = [...allCurrencies].sort();

  // Detect dominant relationship for double-counting note
  const equalsCount = (families || []).filter(
    (f) => f.relationship === 'parent_equals_children_sum'
  ).length;

  return (
    <Section title="Revenue impact">
      {currencies.map((cur) => {
        const parents = impact.parent_with_children_parents?.[cur];
        const children = impact.parent_with_children_children?.[cur];
        const parentOnly = impact.parent_only?.[cur];
        const other = impact.other?.[cur];
        const orphan = impact.orphan_children?.[cur];

        return (
          <div key={cur} className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground">{cur}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {parents && (
                <div className="rounded-lg border p-3 border-amber-200 dark:border-amber-800">
                  <p className="text-xs text-muted-foreground">Parents (with children)</p>
                  <p className="text-lg font-bold tabular-nums">{fmt(parents.client_total)}</p>
                  <p className="text-[10px] text-muted-foreground">{parents.count} bookings</p>
                </div>
              )}
              {children && (
                <div className="rounded-lg border p-3 border-amber-200 dark:border-amber-800">
                  <p className="text-xs text-muted-foreground">Children (of parents)</p>
                  <p className="text-lg font-bold tabular-nums">{fmt(children.client_total)}</p>
                  <p className="text-[10px] text-muted-foreground">{children.count} bookings</p>
                </div>
              )}
              <div className="rounded-lg border p-3">
                <p className="text-xs text-muted-foreground">Everything else</p>
                <p className="text-lg font-bold tabular-nums">
                  {fmt(
                    (parentOnly?.client_total || 0) +
                    (other?.client_total || 0) +
                    (orphan?.client_total || 0)
                  )}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {(parentOnly?.count || 0) + (other?.count || 0) + (orphan?.count || 0)} bookings
                </p>
              </div>
            </div>
            {equalsCount > 0 && parents && (
              <div className="flex items-start gap-2 p-3 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 text-xs text-amber-700 dark:text-amber-300">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <span>
                  Double-counting risk: {cur} {fmt(parents.client_total)} of revenue
                  is counted twice across heatmap and KPI surfaces.
                </span>
              </div>
            )}
          </div>
        );
      })}
    </Section>
  );
}

function InvoicePatternSection({ pattern }) {
  const suffixDist = pattern.suffix_distribution;
  const hasSuffixes = suffixDist && typeof suffixDist === 'object' && suffixDist !== 'none';

  return (
    <Section title="Invoice pattern observations">
      <div className="space-y-3">
        {/* ID samples */}
        {pattern.id_samples && pattern.id_samples.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-1">
              Sample invoice IDs ({pattern.id_samples.length})
            </p>
            <div className="text-xs font-mono bg-muted/30 border rounded-lg p-2 max-h-24 overflow-y-auto break-all">
              {pattern.id_samples.join(', ')}
            </div>
          </div>
        )}

        {/* Suffix distribution */}
        {hasSuffixes && (
          <div>
            <p className="text-xs text-muted-foreground mb-1">Suffix distribution</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(suffixDist)
                .sort((a, b) => b[1] - a[1])
                .map(([suffix, count]) => (
                  <Badge key={suffix} variant="outline" className="font-mono text-xs">
                    -{suffix}: {count}
                  </Badge>
                ))
              }
            </div>
          </div>
        )}
        {!hasSuffixes && (
          <p className="text-xs text-muted-foreground">No child-pattern suffixes detected in invoices.</p>
        )}

        {/* Proforma/final detection */}
        <div className="flex items-start gap-2 text-xs">
          {pattern.suspected_proforma_final ? (
            <>
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0 mt-0.5" />
              <span>
                <span className="font-medium">Suspected proforma/final pattern: Yes.</span>{' '}
                {pattern.proforma_explanation}
              </span>
            </>
          ) : (
            <>
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600 shrink-0 mt-0.5" />
              <span>
                <span className="font-medium">Suspected proforma/final pattern: No.</span>{' '}
                {pattern.proforma_explanation || 'No child-pattern invoices detected.'}
              </span>
            </>
          )}
        </div>

        {/* Counts summary */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-lg border p-2">
            <p className="text-[10px] text-muted-foreground">Parent pattern</p>
            <p className="text-sm font-bold tabular-nums">{pattern.parent_pattern_count?.toLocaleString()}</p>
          </div>
          <div className="rounded-lg border p-2">
            <p className="text-[10px] text-muted-foreground">Child pattern</p>
            <p className="text-sm font-bold tabular-nums">{pattern.child_pattern_count?.toLocaleString()}</p>
          </div>
          <div className="rounded-lg border p-2">
            <p className="text-[10px] text-muted-foreground">Other</p>
            <p className="text-sm font-bold tabular-nums">{pattern.other_pattern_count?.toLocaleString()}</p>
          </div>
        </div>
      </div>
    </Section>
  );
}
